// Serviços de APIs públicas

export interface ViaCEPResponse {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  localidade: string
  uf: string
  erro?: boolean
}

export interface ReceitaWSResponse {
  status: string
  message?: string
  cnpj: string
  nome: string
  fantasia: string
  abertura: string
  situacao: string
  tipo: string
  porte: string
  natureza_juridica: string
  atividade_principal: Array<{ code: string; text: string }>
  atividades_secundarias: Array<{ code: string; text: string }>
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  municipio: string
  uf: string
  cep: string
  email: string
  telefone: string
  efr: string
  motivo_situacao: string
  situacao_especial: string
  data_situacao_especial: string
  capital_social: string
  qsa: Array<{
    nome: string
    qual: string
    pais_origem: string
    nome_rep_legal: string
    qual_rep_legal: string
  }>
  billing: {
    free: boolean
    database: boolean
  }
}

export const buscarCEP = async (cep: string): Promise<ViaCEPResponse | null> => {
  try {
    // Se offline, retorna null silenciosamente
    if (!navigator.onLine) {
      console.log('📡 Offline - busca de CEP não disponível')
      return null
    }

    const cepLimpo = cep.replace(/\D/g, '')
    if (cepLimpo.length !== 8) return null

    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`)
    const data = await response.json()

    if (data.erro) return null

    return data
  } catch (error) {
    // Não loga erro se estiver offline
    if (navigator.onLine) {
      console.error('Erro ao buscar CEP:', error)
    }
    return null
  }
}

export const buscarCNPJ = async (cnpj: string): Promise<ReceitaWSResponse | null> => {
  try {
    // Se offline, retorna null silenciosamente
    if (!navigator.onLine) {
      console.log('📡 Offline - busca de CNPJ não disponível')
      return null
    }

    const cnpjLimpo = cnpj.replace(/\D/g, '')
    if (cnpjLimpo.length !== 14) return null

    // Usando proxy CORS gratuito da BrasilAPI (API pública brasileira)
    const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`)
    
    if (!response.ok) {
      console.error('Erro ao buscar CNPJ:', response.statusText)
      return null
    }

    const data = await response.json()

    // Adaptando o formato da BrasilAPI para o formato ReceitaWS
    const adapted: ReceitaWSResponse = {
      status: 'OK',
      cnpj: data.cnpj,
      nome: data.razao_social || '',
      fantasia: data.nome_fantasia || data.razao_social || '',
      abertura: data.data_inicio_atividade || '',
      situacao: data.descricao_situacao_cadastral || '',
      tipo: data.descricao_tipo_de_logradouro || '',
      porte: data.porte || '',
      natureza_juridica: data.natureza_juridica || '',
      atividade_principal: data.cnae_fiscal_principal ? [{ code: data.cnae_fiscal_principal.codigo, text: data.cnae_fiscal_principal.descricao }] : [],
      atividades_secundarias: data.cnaes_secundarios?.map((cnae: any) => ({ code: cnae.codigo, text: cnae.descricao })) || [],
      logradouro: data.logradouro || '',
      numero: data.numero || '',
      complemento: data.complemento || '',
      bairro: data.bairro || '',
      municipio: data.municipio || '',
      uf: data.uf || '',
      cep: data.cep || '',
      email: data.email || '',
      telefone: data.ddd_telefone_1 || '',
      efr: '',
      motivo_situacao: data.motivo_situacao_cadastral || '',
      situacao_especial: '',
      data_situacao_especial: '',
      capital_social: data.capital_social ? data.capital_social.toString() : '',
      qsa: data.qsa?.map((socio: any) => ({
        nome: socio.nome_socio || '',
        qual: socio.qualificacao_socio || '',
        pais_origem: socio.pais_origem || '',
        nome_rep_legal: socio.nome_representante_legal || '',
        qual_rep_legal: socio.qualificacao_representante_legal || ''
      })) || [],
      billing: {
        free: true,
        database: true
      }
    }

    return adapted
  } catch (error) {
    // Não loga erro se estiver offline
    if (navigator.onLine) {
      console.error('Erro ao buscar CNPJ:', error)
    }
    return null
  }
}
